import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Settings, APP_CONFIG } from 'src/app/config/settings';
import { combine } from 'src/app/shared/utilities/route';
import { FacilityBillingRate } from 'src/app/facility-contracts/facilities/models/facility-billing-rate.model';
import { FacilityHolidayRules } from 'src/app/facility-contracts/facilities/models/facility-holiday-rules.model';
import { FacilityCopyBillingRules } from 'src/app/facility-contracts/facilities/models/facility-copy-billing-rules.model';
import { Holiday } from 'src/app/facility-contracts/facilities/models/holiday.model';
import { FacilityProfession } from 'src/app/facility-contracts/facilities/models/facility-profession.model';
import { FacilityBillingRules } from 'src/app/facility-contracts/facilities/models/facility-billing-rules.model';
import { FacilityBillingRule } from 'src/app/facility-contracts/facilities/models/facility-billing-rule.model';
import { Observable } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class FacilityBillingService {
    private coreApiPath: string;

    constructor(
        private readonly http: HttpClient,
        @Inject(APP_CONFIG) settings: Settings
    ) {
        this.coreApiPath = combine(settings.coreAPIUrl, 'ayanova', 'facility-billing');
    }

    getBillingRatesByFacilityId(facilityId: number): Observable<FacilityBillingRate[]> {
        const url = `${this.coreApiPath}/${facilityId}/rates`;
        return this.http.get<FacilityBillingRate[]>(url);
    }

    getHolidayRulesByFacilityId(facilityId: number): Observable<FacilityHolidayRules> {
        const url = `${this.coreApiPath}/${facilityId}/holiday-rules`;
        return this.http.get<FacilityHolidayRules>(url);
    }

    removeBillingRate(facilityId: number, rateId: number): Observable<unknown> {
        const url = `${this.coreApiPath}/${facilityId}/rates/${rateId}`;
        return this.http.delete(url);
    }

    copyBillingRules(facilityId: number, copyRules: FacilityCopyBillingRules): Observable<unknown> {
        const url = `${this.coreApiPath}/${facilityId}/copy`;
        return this.http.post(url, copyRules);
    }

    getHolidays(): Observable<Holiday[]> {
        const url = `${this.coreApiPath}/holidays`;
        return this.http.get<Holiday[]>(url);
    }

    getProfessionsByFacilityId(facilityId: number): Observable<FacilityProfession[]> {
        const url = `${this.coreApiPath}/${facilityId}/professions`;
        return this.http.get<FacilityProfession[]>(url);
    }

    addProfession(facilityId: number, profession: FacilityProfession): Observable<unknown> {
        const url = `${this.coreApiPath}/${facilityId}/rates`;
        return this.http.post(url, profession);
    }

    updateProfession(facilityId: number, rateId: number, profession: FacilityProfession): Observable<unknown> {
        const url = `${this.coreApiPath}/${facilityId}/rates/${rateId}`;
        return this.http.put(url, profession);
    }

    saveBillingRateChanges(facilityId: number, billingRates: FacilityBillingRate[]): Observable<unknown> {
        const url = `${this.coreApiPath}/${facilityId}/rates`;
        return this.http.put(url, billingRates);
    }

    getRulesByFacilityId(facilityId: number): Observable<FacilityBillingRules> {
        const url = `${this.coreApiPath}/${facilityId}/rules`;
        return this.http.get<FacilityBillingRules>(url);
    }

    updateRules(facilityId: number, rules: FacilityBillingRules): Observable<FacilityBillingRule> {
        const url = `${this.coreApiPath}/${facilityId}/rules`;
        return this.http.put<FacilityBillingRule>(url, rules);
    }

    updateHolidayRules(facilityId: number, rules: FacilityHolidayRules): Observable<number> {
        const url = `${this.coreApiPath}/${facilityId}/holiday-rules`;
        return this.http.post<number>(url, rules);
    }
}
